import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { AuthService } from '../../services/auth.service';
import { AppliedTimeOff } from '../../models/time-off.model';
import { TimeOffApprovalsService } from 'src/app/time-off-approvals/time-off-approvals.service';
import { TimeOffService } from 'src/app/time-off/time-off.service';

@Component({
  selector: 'app-time-off-info',
  templateUrl: './time-off-info.component.html',
  styleUrls: ['./time-off-info.component.scss']
})
export class TimeOffInfoComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  isSubmitted = false;
  isApproved = false;
  isRejected = false;
  timeOffActionsForm = new FormGroup({
    comments: new FormControl('')
  });

  showCancelButton = false;
  showApproveButton = false;
  showRejectButton = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TimeOffDetailDialogData,
    private timeOffService: TimeOffService,
    private timeOffApprovalsService: TimeOffApprovalsService,
    private dialogRef: MatDialogRef<TimeOffInfoComponent>,
    private snackBar: MatSnackBar,
    private authService: AuthService
  ) {}

  ngOnInit() {
    if (this.data.mode === 'VIEW') {
      this.showCancelButton = this.hasPermission('timeoff_cancel');
    }
    else if (this.data.mode === 'APPROVALS') {
      this.showApproveButton = this.hasPermission('timeoff_approve');
      this.showRejectButton = this.hasPermission('timeoff_reject');
    }
  }

  cancelTimeOff() {
    let cancelConfirmatinDialogRef = this.timeOffService.openCancelConfirmationDialogBox(this.data.timeOff.TIMEOFF_ID);

    cancelConfirmatinDialogRef.afterClosed().pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: () => this.dialogRef.close()
    });
  }

  approveTimeOff(timeOff: AppliedTimeOff) {
    if (this.isSubmitted) return;

    if(!this.timeOffActionsForm.valid) {
      return;
    }

    this.isApproved = true;
    this.isRejected = false;

    this.isSubmitted = true;
    const comments = this.timeOffActionsForm.value['comments'];

    this.timeOffService.approveTimeOff(timeOff, comments).pipe(takeUntil(this.destroy$)).subscribe({
      next: (message: string) => {
        this.snackBar.open(message,  '✖', {
          duration: 5000,
          panelClass: ['green-snackbar'],
        });

        this.dialogRef.close();
        if(this.data.mode === 'APPROVALS') {
          this.timeOffApprovalsService.loadApprovalsData();
        }
        else {
          this.timeOffService.loadAllData();
        }
      },
      error: () => {
        this.isSubmitted = false;
        this.isApproved = false;
      }
    });
  }

  rejectTimeOff(timeOff: AppliedTimeOff) {
    if (this.isSubmitted) return;
    this.isRejected = true;
    this.isApproved = false;

    if(!this.timeOffActionsForm.valid || 
      this.timeOffActionsForm.controls['comments'].value === undefined || 
      this.timeOffActionsForm.controls['comments'].value === null || 
      this.timeOffActionsForm.controls['comments'].value === '') {
        this.isRejected = false;
        this.snackBar.open('Comments is required.',  '✖', {
          duration: 5000,
          panelClass: ['red-snackbar'],
        });
        return;
    }

    this.isSubmitted = true;
    const comments = this.timeOffActionsForm.value['comments'];

    this.timeOffService.rejectTimeOff(timeOff, comments).pipe(takeUntil(this.destroy$)).subscribe({
      next: (message: string) => {
        this.snackBar.open(message,  '✖', {
          duration: 5000,
          panelClass: ['green-snackbar'],
        });

        this.dialogRef.close();
        if(this.data.mode === 'APPROVALS') {
          this.timeOffApprovalsService.loadApprovalsData();
        }
        else {
          this.timeOffService.loadAllData();
        }
      },
      error: () => {
        this.isSubmitted = false;
        this.isRejected = false;
      }
    });
  }

  hasPermission(perm: string) {
    try {
        let loggedInUserPermissions = this.authService.getStaticData('UI_PERMS');
        return loggedInUserPermissions.includes(perm);
    } catch (error) {
        console.error('Failed to get user permissions:', error);
        return false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

interface TimeOffDetailDialogData {
  mode: string,
  timeOff: AppliedTimeOff
}
